import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Breadcrumb,
    Card,
    Col,
    Row,
    Badge,
    Spinner,
    Form,
    Button,
    Alert,
    FormCheck,
} from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import {
    applyOptimisticPinToTasks,
    formatGanttDateTime,
    formatTimelineScopeRange,
    isActiveTeamMember,
    stampGanttQueueOrder,
    restampGanttQueueOrder,
} from './ganttHelpers';
import TeamStyleGanttChart, {
    useTeamStyleTimeline,
    userFullName,
} from '../../components/TeamStyleGanttChart';
import TimelineRunOrder from '../../components/TimelineRunOrder';
import '../../assets/css/user-gantt.css';
import '../../assets/css/team-planner.css';
import '../../assets/css/timeline-ui.css';

const reorderList = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const AdminUserGantt = () => {
    const { accessToken } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [selectedUserId, setSelectedUserId] = useState(searchParams.get('user_id') || '');
    const [targetUser, setTargetUser] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [, setConflicts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [orderSaving, setOrderSaving] = useState(false);
    const [orderMessage, setOrderMessage] = useState('');

    const triggerCardId = searchParams.get('trigger_card_id') || '';
    const [reschedulePreview, setReschedulePreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [selectedShiftIds, setSelectedShiftIds] = useState(new Set());
    const [applyLoading, setApplyLoading] = useState(false);
    const [applyMessage, setApplyMessage] = useState('');
    const [applyMessageVariant, setApplyMessageVariant] = useState('success');
    const [highlightedCardIds, setHighlightedCardIds] = useState(new Set());
    const [pinSaving, setPinSaving] = useState(false);
    const [pinMessage, setPinMessage] = useState('');

    useEffect(() => {
        if (!accessToken) return;
        const loadUsers = async () => {
            setUsersLoading(true);
            try {
                const res = await axios.get(`${config.apiBASEURL}/TaskBoardRoutes/fetch-all-users`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const activeUsers = (res.data || []).filter(isActiveTeamMember);
                setUsers(activeUsers);
                const currentUserId = searchParams.get('user_id') || '';
                if (currentUserId && !activeUsers.some((u) => String(u.user_id) === String(currentUserId))) {
                    setSelectedUserId('');
                    const next = new URLSearchParams(searchParams);
                    next.delete('user_id');
                    setSearchParams(next);
                }
            } catch (err) {
                console.error('Failed to load users', err);
                setError('Could not load team members.');
            } finally {
                setUsersLoading(false);
            }
        };
        loadUsers();
    }, [accessToken]);

    const loadReschedulePreviewForData = useCallback(
        async (spillConflicts, taskList) => {
            if (!selectedUserId || !accessToken) {
                setReschedulePreview(null);
                return;
            }
            setPreviewLoading(true);
            try {
                const spillCardIds = new Set(
                    spillConflicts
                        .filter((c) => c.type === 'exceeded_allotment' || Number(c.spill_hours) > 0)
                        .map((c) => Number(c.card_id))
                        .filter(Boolean)
                );
                taskList.forEach((t) => {
                    if (Number(t.bandwidth_spill_hours) > 0) {
                        spillCardIds.add(Number(t.id));
                    }
                });

                if (triggerCardId) {
                    const res = await axios.post(
                        `${config.apiBASEURL}/TaskBoardRoutes/schedule/priority-preview`,
                        {
                            user_id: Number(selectedUserId),
                            trigger_card_id: Number(triggerCardId),
                        },
                        { headers: { Authorization: `Bearer ${accessToken}` } }
                    );
                    setReschedulePreview({
                        mode: 'priority',
                        affected_changes: res.data?.affected_changes || [],
                        unchanged_tasks: res.data?.unchanged_tasks || [],
                    });
                    setSelectedShiftIds(
                        new Set((res.data?.affected_changes || []).map((c) => c.card_id))
                    );
                    return;
                }

                const res = await axios.post(
                    `${config.apiBASEURL}/TaskBoardRoutes/schedule/preview`,
                    { user_id: Number(selectedUserId) },
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                const allChanges = res.data?.changes || res.data?.impact_summary?.changes || [];
                let changes = allChanges.filter(
                    (c) =>
                        Number(c.bandwidth_spill_hours) > 0 ||
                        spillCardIds.has(Number(c.card_id))
                );
                if (!changes.length && spillCardIds.size) {
                    changes = allChanges.filter((c) => spillCardIds.has(Number(c.card_id)));
                }
                if (!changes.length && spillCardIds.size) {
                    changes = [...spillCardIds].map((cardId) => {
                        const task = taskList.find((t) => Number(t.id) === cardId);
                        const conflict = spillConflicts.find(
                            (c) => Number(c.card_id) === cardId
                        );
                        const proposed =
                            task?.scheduled_end_at || task?.timeline_end_at || null;
                        return {
                            card_id: cardId,
                            title: conflict?.title || task?.title || `Task #${cardId}`,
                            before: { due_date: task?.deadline_at || null },
                            after: { due_date: proposed },
                            bandwidth_spill_hours: conflict?.spill_hours || null,
                        };
                    }).filter((c) => c.after.due_date);
                }

                setReschedulePreview({
                    mode: 'schedule',
                    affected_changes: changes,
                    unchanged_tasks: [],
                });
                setSelectedShiftIds(new Set(changes.map((c) => c.card_id)));
            } catch (err) {
                console.error('Reschedule preview failed', err);
                setReschedulePreview(null);
            } finally {
                setPreviewLoading(false);
            }
        },
        [selectedUserId, triggerCardId, accessToken]
    );

    const loadTimeline = useCallback(async ({ silent = false } = {}) => {
        if (!selectedUserId || !accessToken) {
            setTasks([]);
            setTargetUser(null);
            setReschedulePreview(null);
            return;
        }
        if (silent) setRefreshing(true);
        else {
            setLoading(true);
            setError('');
            setTasks([]);
            setReschedulePreview(null);
        }
        try {
            const res = await axios.get(
                `${config.apiBASEURL}/TaskBoardRoutes/gantt/admin/user/${selectedUserId}`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            const loadedTasks = stampGanttQueueOrder(res.data?.tasks || []);
            const loadedConflicts = res.data?.conflicts || [];
            setTasks(loadedTasks);
            setConflicts(loadedConflicts);
            setTargetUser(res.data?.user || null);
            await loadReschedulePreviewForData(loadedConflicts, loadedTasks);
        } catch (err) {
            console.error('Error loading admin user gantt:', err);
            if (!silent) {
                setError(err.response?.data?.message || 'Could not load user timeline.');
                setTasks([]);
                setReschedulePreview(null);
            }
        } finally {
            if (silent) setRefreshing(false);
            else setLoading(false);
        }
    }, [selectedUserId, accessToken, loadReschedulePreviewForData]);

    useEffect(() => {
        loadTimeline();
    }, [loadTimeline]);

    const previewMap = useMemo(() => {
        const map = new Map();
        (reschedulePreview?.affected_changes || []).forEach((c) => {
            map.set(c.card_id, c.after?.due_date);
        });
        return map;
    }, [reschedulePreview]);

    const { rangeStart, rangeEnd } = useTeamStyleTimeline(tasks, 1, previewMap);

    const members = useMemo(() => {
        if (!targetUser && !selectedUserId) return [];
        const user =
            targetUser ||
            users.find((u) => String(u.user_id) === String(selectedUserId)) || {
                user_id: selectedUserId,
                first_name: 'Selected',
                last_name: 'user',
            };
        return [{
            user,
            tasks: tasks.map((t) => ({
                ...t,
                gantt_user_id: t.gantt_user_id || user.user_id,
            })),
        }];
    }, [targetUser, selectedUserId, users, tasks]);

    const handleUserChange = (userId) => {
        setSelectedUserId(userId);
        setReschedulePreview(null);
        setApplyMessage('');
        const next = new URLSearchParams(searchParams);
        if (userId) next.set('user_id', userId);
        else next.delete('user_id');
        setSearchParams(next);
    };

    const handleDragEnd = async (result) => {
        if (!result.destination || result.source.index === result.destination.index) return;
        const reordered = restampGanttQueueOrder(
            reorderList(tasks, result.source.index, result.destination.index)
        );
        setTasks(reordered);
        if (!selectedUserId || !accessToken) return;
        setOrderSaving(true);
        setOrderMessage('');
        try {
            await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/manual-order`,
                {
                    user_id: Number(selectedUserId),
                    ordered_card_ids: reordered.map((t) => t.id),
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setOrderMessage('Task order saved — higher items run first in the schedule.');
            await loadTimeline({ silent: true });
        } catch (err) {
            setOrderMessage(err.response?.data?.message || 'Could not save order.');
            await loadTimeline({ silent: true });
        } finally {
            setOrderSaving(false);
        }
    };

    const savePinStart = async (task, preferredStartAt, allocatedHours = null) => {
        if (!selectedUserId || !accessToken || !task?.id) return false;
        if (preferredStartAt) {
            setTasks((prev) =>
                applyOptimisticPinToTasks(
                    prev,
                    task.id,
                    preferredStartAt,
                    selectedUserId,
                    allocatedHours,
                    false
                )
            );
        }
        setPinSaving(true);
        setPinMessage('');
        try {
            const pinRes = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/pin-start`,
                {
                    user_id: Number(selectedUserId),
                    card_id: Number(task.id),
                    preferred_start_at: preferredStartAt,
                    allow_outside_window: true,
                    bump_others: false,
                    ...(allocatedHours != null ? { allocated_hours: allocatedHours } : {}),
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setPinMessage(
                preferredStartAt
                    ? pinRes.data?.message ||
                          (allocatedHours != null
                              ? `Updated “${task.title}” duration to ${allocatedHours}h.`
                              : `Pinned “${task.title}” start.`)
                    : `Cleared pin on “${task.title}”.`
            );
            setHighlightedCardIds(new Set([Number(task.id)]));
            setTimeout(() => setHighlightedCardIds(new Set()), 4000);
            return true;
        } catch (err) {
            setPinMessage(err.response?.data?.message || 'Could not pin start time.');
            await loadTimeline({ silent: true });
            return false;
        } finally {
            setPinSaving(false);
        }
    };

    const toggleShiftSelection = (cardId) => {
        setSelectedShiftIds((prev) => {
            const next = new Set(prev);
            if (next.has(cardId)) next.delete(cardId);
            else next.add(cardId);
            return next;
        });
    };

    const handleApplyPriority = async () => {
        const changes = (reschedulePreview?.affected_changes || [])
            .filter((c) => selectedShiftIds.has(c.card_id))
            .map((c) => ({
                card_id: c.card_id,
                due_date: c.after.due_date,
            }));
        if (!changes.length || !accessToken) return;
        setApplyLoading(true);
        setApplyMessage('');
        setApplyMessageVariant('success');
        try {
            const res = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/priority-apply`,
                { user_id: Number(selectedUserId), changes },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            const applied = res.data?.applied || [];
            const detailLines = applied.map((a) => {
                const before = a.previous_due_date
                    ? formatGanttDateTime(a.previous_due_date)
                    : '—';
                const after = formatGanttDateTime(a.new_due_date);
                return `• ${a.title}: ${before} → ${after}`;
            });
            setApplyMessage(
                detailLines.length
                    ? `Due date(s) updated (work start times unchanged — drag rows to reorder):\n${detailLines.join('\n')}`
                    : res.data?.message || 'Reschedule applied.'
            );
            setHighlightedCardIds(new Set(applied.map((a) => Number(a.card_id))));
            setTimeout(() => setHighlightedCardIds(new Set()), 5000);
            setReschedulePreview(null);
            const next = new URLSearchParams(searchParams);
            next.delete('trigger_card_id');
            setSearchParams(next);
            await loadTimeline({ silent: true });
        } catch (err) {
            setApplyMessageVariant('danger');
            setApplyMessage(err.response?.data?.message || 'Apply failed.');
        } finally {
            setApplyLoading(false);
        }
    };

    const userLabel = targetUser ? userFullName(targetUser) : '';

    return (
        <Row className="body_content user-gantt-page team-planner-page">
            <Col lg={12} className="mb-3">
                <Breadcrumb>
                    <Breadcrumb.Item onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
                        <i className="pi pi-angle-left" /> Back
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>User Timeline (all projects)</Breadcrumb.Item>
                </Breadcrumb>
            </Col>

            <Col lg={12}>
                <Card>
                    <Card.Body>
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                            <Form.Label className="mb-0 me-1 fw-semibold small">Team member</Form.Label>
                            {usersLoading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <Form.Select
                                    style={{ minWidth: 280, maxWidth: 420 }}
                                    value={selectedUserId}
                                    onChange={(e) => handleUserChange(e.target.value)}
                                >
                                    <option value="">Select a user…</option>
                                    {users.map((u) => (
                                        <option key={u.user_id} value={u.user_id}>
                                            {`${u.first_name || ''} ${u.last_name || ''}`.trim() ||
                                                u.email}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                            {orderSaving && (
                                <span className="small text-muted">Saving order…</span>
                            )}
                            {pinSaving && (
                                <span className="small text-muted">Saving start pin…</span>
                            )}
                        </div>

                        {triggerCardId && selectedUserId && (
                            <Alert variant="primary" className="py-2 small">
                                <strong>Priority task added</strong> — review downstream tasks that may
                                need rescheduling. Drag rows to set what runs first; only checked tasks
                                will move when you apply.
                                {previewLoading && (
                                    <span className="ms-2">
                                        <Spinner animation="border" size="sm" />
                                    </span>
                                )}
                            </Alert>
                        )}

                        <Alert variant="light" className="py-2 small border mb-3">
                            Same layout as Team Planner / My Timeline: <strong>Queue</strong> (⋮⋮)
                            sets what runs first; <strong>timeline</strong> shows labeled bars — drag a
                            bar to pin its start. Changes notify the assignee. Use Apply below to update
                            due dates when needed.
                        </Alert>

                        {reschedulePreview?.mode === 'priority' &&
                            reschedulePreview?.unchanged_tasks?.length > 0 && (
                            <Alert variant="secondary" className="py-2 small mb-3">
                                <strong>{reschedulePreview.unchanged_tasks.length} task(s) stay in place</strong>{' '}
                                (before the new priority task in the queue).
                            </Alert>
                        )}

                        {reschedulePreview?.affected_changes?.length > 0 && (
                            <div className="mb-3 p-3 border rounded bg-white">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong className="small">
                                        {reschedulePreview.mode === 'priority'
                                            ? `Downstream tasks to reschedule (${reschedulePreview.affected_changes.length})`
                                            : `Overloaded tasks — extend due dates (${reschedulePreview.affected_changes.length})`}
                                        {previewLoading && (
                                            <Spinner animation="border" size="sm" className="ms-2" />
                                        )}
                                    </strong>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() =>
                                                setSelectedShiftIds(
                                                    new Set(
                                                        reschedulePreview.affected_changes.map(
                                                            (c) => c.card_id
                                                        )
                                                    )
                                                )
                                            }
                                        >
                                            Select all
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setSelectedShiftIds(new Set())}
                                        >
                                            Clear
                                        </Button>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            disabled={
                                                applyLoading || selectedShiftIds.size === 0
                                            }
                                            onClick={handleApplyPriority}
                                        >
                                            {applyLoading
                                                ? 'Applying…'
                                                : `Apply (${selectedShiftIds.size})`}
                                        </Button>
                                    </div>
                                </div>
                                <table className="user-gantt-impact-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 36 }} />
                                            <th>Task</th>
                                            <th>Before</th>
                                            <th>After</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reschedulePreview.affected_changes.map((c) => (
                                            <tr key={c.card_id}>
                                                <td>
                                                    <FormCheck
                                                        type="checkbox"
                                                        checked={selectedShiftIds.has(c.card_id)}
                                                        onChange={() =>
                                                            toggleShiftSelection(c.card_id)
                                                        }
                                                        aria-label={`Reschedule ${c.title}`}
                                                    />
                                                </td>
                                                <td>{c.title}</td>
                                                <td>
                                                    {c.before?.due_date
                                                        ? moment(c.before.due_date).format(
                                                              'DD MMM HH:mm'
                                                          )
                                                        : '—'}
                                                </td>
                                                <td>
                                                    {moment(c.after.due_date).format(
                                                        'DD MMM HH:mm'
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {reschedulePreview &&
                            !previewLoading &&
                            !reschedulePreview.affected_changes?.length &&
                            triggerCardId && (
                                <Alert variant="success" className="py-2 small mb-3">
                                    No downstream tasks need rescheduling for this priority assignment.
                                </Alert>
                            )}

                        {orderMessage && (
                            <Alert variant="info" className="py-2 small">
                                {orderMessage}
                            </Alert>
                        )}
                        {pinMessage && (
                            <Alert variant="success" className="py-2 small">
                                {pinMessage}
                            </Alert>
                        )}
                        {applyMessage && (
                            <Alert variant={applyMessageVariant} className="py-2 small">
                                <span className="admin-gantt-apply-message">{applyMessage}</span>
                            </Alert>
                        )}

                        {loading && !tasks.length && (
                            <div className="text-center py-5">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading timeline…
                            </div>
                        )}

                        {!loading && error && <p className="text-danger">{error}</p>}

                        {!loading && !error && selectedUserId && tasks.length === 0 && (
                            <p className="text-muted mb-0">No active assigned cards for this user.</p>
                        )}

                        {!error && tasks.length > 0 && (
                            <TeamStyleGanttChart
                                title="User timeline"
                                subtitle={`${userLabel} · ${formatTimelineScopeRange(
                                    rangeStart,
                                    rangeEnd
                                )} · all projects`}
                                activeCount={tasks.length}
                                members={members}
                                skipRegroup
                                conflictPersonName={userLabel}
                                sidePanel={
                                    <TimelineRunOrder
                                        tasks={tasks}
                                        onDragEnd={handleDragEnd}
                                        droppableId="admin-user-gantt-queue"
                                        triggerCardId={triggerCardId}
                                        extraMeta={(task) =>
                                            previewMap.has(task.id) ? (
                                                <Badge bg="info">Will shift</Badge>
                                            ) : null
                                        }
                                    />
                                }
                                previewMap={previewMap}
                                highlightedCardIds={highlightedCardIds}
                                onDateChange={async (ganttTask) => {
                                    const source = ganttTask?.sourceTask;
                                    if (!source) return false;
                                    const start =
                                        ganttTask.start instanceof Date
                                            ? ganttTask.start.toISOString()
                                            : ganttTask.start;
                                    return savePinStart(
                                        source,
                                        start,
                                        ganttTask.isResize ? ganttTask.hours : null
                                    );
                                }}
                                onBarClick={(t) => {
                                    if (t?.board_id && t?.id) {
                                        navigate(
                                            `/Graphe/dashboard/kanbanboard/${t.board_id}?card=${t.id}`
                                        );
                                    }
                                }}
                                refreshing={refreshing || pinSaving || orderSaving}
                            />
                        )}

                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default AdminUserGantt;
